import type { ItineraryInterface, ItineraryService } from '@/types/trips'

/**
 * Identity helpers for records the API has not seen yet.
 *
 * A row built in the planner has no uuid until it is saved, so comparing uuids
 * resolves `undefined === undefined` to true and matches the wrong record —
 * typically the first other unsaved row in the trip. Each helper here either
 * falls back to something that still tells unsaved records apart, or reports no
 * match rather than guessing.
 */

/**
 * True when both arguments refer to the same record. Saved records compare by
 * uuid; an unsaved one only ever matches itself.
 */
export const isSameRecord = (
  candidate: ItineraryService | null | undefined,
  target: ItineraryService | null | undefined,
): boolean => {
  if (!candidate || !target) return false

  return target.uuid
    ? (candidate.uuid === target.uuid)
    : (candidate === target)
}

/**
 * The day a service is scheduled on. Prefers the uuid the service carries; for a
 * day that was never persisted, the only remaining handle is the service itself,
 * so fall back to whichever day still holds it. Returns null when neither
 * applies — a service already lifted off its day has no day to act on.
 */
export const findScheduledDay = (
  itinerary: Array<ItineraryInterface> | undefined | null,
  service: ItineraryService | null | undefined,
): ItineraryInterface | null => {
  if (!service) return null

  const dayUuid = service.tripDay?.uuid
  if (dayUuid) {
    return itinerary?.find((day: ItineraryInterface) => day.uuid === dayUuid) ?? null
  }

  return itinerary?.find((day: ItineraryInterface) => {
    return day.services?.some((candidate: ItineraryService) => candidate === service) ?? false
  }) ?? null
}

/**
 * The services on a day that are bundled under the given parent. Children point
 * at their parent by uuid, so a parent that has never been saved cannot have any
 * yet — matching on its undefined uuid would instead return every unsaved
 * top-level row on the day, all of which carry `parent: undefined`.
 */
export const linkedServicesOf = (
  day: ItineraryInterface | null | undefined,
  parent: ItineraryService | null | undefined,
): Array<ItineraryService> => {
  if (!parent?.uuid) return []

  return day?.services?.filter((candidate: ItineraryService) => candidate.parent === parent.uuid) ?? []
}
